import { useEffect, useState } from 'react';
import moment from 'moment';
import BottomSheet from './ui/BottomSheet';
import PillGroup from './ui/PillGroup';

const PERIODS = [{ value: 2, label: 'Last 2 days' }, { value: 5, label: 'Last 5 days' }, { value: 10, label: 'Last 10 days' }];
const SHOW = [{ value: 'both', label: 'All' }, { value: 'submissions', label: 'Submissions' }, { value: 'pending', label: 'Continuous' }];
const horizonLabel = (h) => (Number(h) === 0 ? 'Intraday' : `D+${h}`);

// Same window the Dashboard seeds: N days back through tomorrow (UTC days).
export const periodRange = (daysBack, now = moment.utc()) => ({
  startDate: now.clone().utc().subtract(daysBack, 'days').startOf('day').format('YYYY-MM-DD'),
  endDate: now.clone().utc().add(1, 'days').startOf('day').format('YYYY-MM-DD'),
});

// Phone chip label for a window: "14 – 17 Sep", or "28 Sep – 1 Oct" when
// the window crosses into another month.
export const periodChipLabel = (startDate, endDate) => {
  const start = moment(startDate);
  const end = moment(endDate);
  return `${start.format(start.isSame(end, 'month') ? 'D' : 'D MMM')} – ${end.format('D MMM')}`;
};

const matchPeriod = (startDate, endDate, now) =>
  PERIODS.map((p) => p.value).find((d) => {
    const r = periodRange(d, now);
    return r.startDate === startDate && r.endDate === endDate;
  }) ?? null;

const sectionTitle = 'text-sm font-semibold text-gray-900 dark:text-gray-100';
// focus:border/ring-brand override the forms plugin's blue focus in light mode;
// the dark color-scheme lightens the native calendar icon on the dark sheet.
const dateInput = 'h-11 w-full rounded-lg border-gray-300 bg-surface px-3 text-base text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-100 dark:[color-scheme:dark]';

export default function ChartOptionsSheet({ open, onClose, horizonFilter, availableHorizons = [], startDate, endDate, previewMode, hasPendingPreview, onApply, now }) {
  const clock = now || moment.utc();
  const [draft, setDraft] = useState({ horizonFilter, startDate, endDate, previewMode });
  useEffect(() => {
    if (open) setDraft({ horizonFilter, startDate, endDate, previewMode });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const horizonOptions = [
    { value: 'latest', label: 'Latest' },
    ...availableHorizons.map((h) => ({ value: String(h), label: horizonLabel(h), dashed: Number(h) >= 2 })),
  ];
  // Native min/max do not stop a cleared or typed date; ISO strings compare in order.
  const validRange = Boolean(draft.startDate && draft.endDate) && draft.startDate <= draft.endDate;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Chart options"
      footer={(
        <button
          type="button"
          disabled={!validRange}
          onClick={() => { onApply(draft); onClose(); }}
          className="h-12 w-full rounded-xl bg-brand-600 text-[15px] font-semibold text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-700 dark:focus-visible:ring-offset-gray-800"
        >
          Apply
        </button>
      )}
    >
      <div className="space-y-6 pt-2">
        <section>
          <h3 className={sectionTitle}>Horizon</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Which forecast to draw for each time</p>
          <div className="mt-2.5">
            <PillGroup ariaLabel="Horizon" options={horizonOptions} value={draft.horizonFilter} onChange={(v) => setDraft({ ...draft, horizonFilter: v })} />
          </div>
        </section>
        <section>
          <h3 className={sectionTitle}>Period</h3>
          <div className="mt-2.5">
            <PillGroup ariaLabel="Period" options={PERIODS} value={matchPeriod(draft.startDate, draft.endDate, clock)} onChange={(d) => setDraft({ ...draft, ...periodRange(d, clock) })} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              From
              <input type="date" value={draft.startDate} max={draft.endDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} className={dateInput} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              To
              <input type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} className={dateInput} />
            </label>
          </div>
        </section>
        {hasPendingPreview && (
          <section>
            <h3 className={sectionTitle}>Show</h3>
            <div className="mt-2.5">
              <PillGroup ariaLabel="Series shown" options={SHOW} value={draft.previewMode} onChange={(v) => setDraft({ ...draft, previewMode: v })} />
            </div>
          </section>
        )}
      </div>
    </BottomSheet>
  );
}
